import { useEffect, useMemo, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

const CATS = [
  { id: "all", label: "All Work" },
  { id: "render-insulation", label: "Render & Insulation" },
  { id: "domestic-extensions", label: "Domestic & Extensions" },
  { id: "commercial", label: "Commercial" },
  { id: "landscaping", label: "Landscaping" },
];

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/gallery").then((res) => {
      if (cancel) return;
      setItems(res.data || []);
    }).catch(() => {
      if (!cancel) setItems([]);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const filtered = useMemo(
    () => active === "all" ? items : items.filter((i) => i.category === active),
    [items, active]
  );

  return (
    <div data-testid="gallery-page">
      <section className="bg-crest-green text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold">Project gallery</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            Recent work,<br />from across the North West.
          </h1>
          <p className="mt-6 max-w-2xl text-white/80 font-body">
            A growing collection of completed Cresttor jobs across our four specialisms. Tap a category to filter.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="flex flex-wrap gap-2 justify-center md:justify-start" role="tablist">
          {CATS.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`gallery-tab-${c.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors duration-200 ${
                active === c.id
                  ? "bg-crest-green text-white border-crest-green"
                  : "border-border text-crest-navy hover:border-crest-green hover:text-crest-green"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="mt-16 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-crest-green" />
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="mt-16 text-center border border-dashed border-border rounded-lg p-12 text-muted-foreground" data-testid="gallery-empty">
                <ImageOff className="h-10 w-10 mx-auto text-crest-navy/30" />
                <h3 className="mt-4 font-display text-xl text-crest-navy">No images yet in this category</h3>
                <p className="mt-2 text-sm">We’re a brand-new company — our gallery will grow as projects complete. Check back soon.</p>
              </div>
            );
          }
          return (
            <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  onClick={() => setLightbox(it)}
                  className="mb-5 block w-full break-inside-avoid rounded-lg overflow-hidden border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group"
                  data-testid={`gallery-item-${it.id}`}
                >
                <div className="overflow-hidden">
                  <img src={fileUrl(it.image_url)} alt={it.title || it.category}
                       className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                {it.title && (
                  <div className="px-4 py-3 text-left">
                    <div className="font-display text-sm font-semibold text-crest-navy">{it.title}</div>
                    <div className="text-xs uppercase tracking-[0.14em] text-crest-gold mt-0.5">
                      {CATS.find((c) => c.id === it.category)?.label}
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
          );
        })()}
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-crest-navy/95 backdrop-blur p-4 md:p-10 flex items-center justify-center cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <img src={fileUrl(lightbox.image_url)} alt={lightbox.title || ""}
               className="max-h-full max-w-full rounded-md shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default Gallery;
